// 2026-05-26 - FormuLearning - 좌측 패널
// 2026-09-21 수정 - 이슈 #77(G6): My Projects 평면 목록 → **폴더 탐색기**(`ProjectExplorer` — 폴더·정렬·
//   드래그 이동·우클릭·이름 변경). 숨김 구획(이슈 #7)은 트리 밖에 현행대로 둔다(BR-PRJ-08).
// 2026-09-23 수정 - 이슈 #82: 진행 중 작업이 있으면 프로젝트 열기·New Project 를 막고 안내한다(프로젝트 전환 잠금).
// 2026-09-26 수정 - 이슈 #95 단계 3-5: 스텝 명칭 → `lib/steps` · 접힌 패널 번호 버튼 툴팁(`title`, UI UX §4.3).
// 2026-11-03 수정 - 이슈 #114: viewer 는 New Project 비활성.
// 파일 위치: frontend/src/components/layout/LeftPanel.tsx

import { useEffect, useState } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { useProjectStore } from '../../stores/projectStore'
import { useUiStore } from '../../stores/uiStore'
import { useBusyStore, blockIfBusy } from '../../stores/busyStore'
import ProjectExplorer from '../../features/project/ProjectExplorer'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'
import { STEP_LABELS, stepCaption } from '../../lib/steps'


const STATUS_COLORS: Record<string, string> = {
  completed: 'var(--color-status-completed)',
  in_progress: 'var(--color-status-in-progress)',
  pending: 'var(--color-status-pending)',
}

function stepStatus(stepNum: number, currentStep: number): string {
  if (stepNum < currentStep) return 'completed'
  if (stepNum === currentStep) return 'in_progress'
  return 'pending'
}

export default function LeftPanel() {
  const user = useAuthStore((s) => s.user)
  const { projects, currentProject, fetchProjects } = useProjectStore()
  const { leftPanelOpen, toggleLeftPanel, openModal, openNewProject, activeStep, setActiveStep, setSelectedProjectId, theme } = useUiStore()

  // 2026-06-19 - 숨김 프로젝트 펼침 토글 (이슈 #7) — 순수 클라이언트 상태
  const [showHidden, setShowHidden] = useState(false)
  const busy = useBusyStore((s) => s.task !== null)
  const canWrite = useCanWrite()

  // 2026-06-19 - 테마별 워드마크 (이슈 #9): 라이트=어두운 글자 원본, 다크=밝은 변형
  const wordmark = theme === 'light' ? '/logo-wordmark.png' : '/logo-wordmark-dark.png'

  // 2026-06-18 - 워크플로 클릭 시 해당 Step으로 스크롤 (우측 도트 ScrollStepper와 동일 동작, 이슈 #1)
  const scrollToStep = (step: number) => {
    setActiveStep(step)
    const el = document.getElementById(`step-${step}`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  // 2026-06-19 - 숨김 여부로 목록 분리 (이슈 #7)
  const visibleProjects = projects.filter((p) => !p.is_hidden)
  const hiddenProjects = projects.filter((p) => p.is_hidden)

  const openProject = (id: string) => {
    if (currentProject?.id === id) {
      // 현재 프로젝트 정보 모달은 열어 둔다 — 이름·메모 편집은 전환이 아니다. Hide·Delete 는 모달이 막는다.
      openModal('project-info')
    } else {
      if (blockIfBusy()) return
      setSelectedProjectId(id)
      openModal('project-load')
    }
  }

  // 숨긴 프로젝트 행 — 트리 밖 «Show hidden» 구획 전용(이슈 #7 · #77 BR-PRJ-08)
  const renderHiddenButton = (p: typeof projects[number]) => (
    <button
      key={p.id}
      onClick={() => openProject(p.id)}
      className="mb-1 flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-bg-input"
      style={{
        ...(currentProject?.id === p.id ? { background: 'var(--bg-row-active)' } : {}),
        opacity: 0.55,
      }}
    >
      <span className="text-text-secondary">🙈</span>
      <span className="truncate text-text-body">{p.name}</span>
      <span className="ml-auto shrink-0 rounded px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-text-secondary" style={{ background: 'var(--bg-input)' }}>
        Hidden
      </span>
    </button>
  )

  const w = leftPanelOpen ? 220 : 64

  return (
    <aside
      data-theme={theme === 'mixed' ? 'dark' : undefined}
      className="flex flex-col border-r transition-all duration-200"
      style={{ width: w, minWidth: w, backgroundColor: 'var(--color-bg-panel)', borderColor: 'var(--border-divider)' }}
    >
      {/* Brand(로고: 아이콘 + 워드마크 가로 배치) + Toggle */}
      <div className="flex h-14 items-center justify-between border-b px-3" style={{ borderColor: 'var(--border-divider)' }}>
        {leftPanelOpen ? (
          <div className="flex min-w-0 items-center gap-2">
            <img src="/logo-icon.png" alt="FormuLearning" className="h-8 w-auto shrink-0" />
            <img src={wordmark} alt="FormuLearning" className="h-5 w-auto min-w-0" />
          </div>
        ) : (
          <img src="/logo-icon.png" alt="FormuLearning" className="h-7 w-auto" />
        )}
        <button onClick={toggleLeftPanel} className="shrink-0 text-text-secondary hover:text-text-body text-sm">
          {leftPanelOpen ? '◀' : '▶'}
        </button>
      </div>

      {!leftPanelOpen ? (
        <div className="flex flex-1 flex-col items-center gap-2 pt-4">
          {STEP_LABELS.map((_, i) => {
            const n = i + 1
            // 2026-07-09 - 접힌 패널도 진행도 점등 반영 (이슈 #33 REQ-B7)
            const lit = !!currentProject && n <= currentProject.current_step
            const isActive = activeStep === n
            return (
              <button
                key={n}
                onClick={() => scrollToStep(n)}
                title={stepCaption(n)}
                className="flex h-8 w-8 items-center justify-center rounded text-xs font-medium"
                style={{
                  color: isActive || lit ? '#fff' : 'var(--color-text-secondary)',
                  background: lit ? 'var(--bg-chip-purple)' : 'transparent',
                  boxShadow: isActive ? 'inset 0 0 0 1.5px var(--color-accent-purple)' : undefined,
                }}
              >
                {n}
              </button>
            )
          })}
        </div>
      ) : (
        <div className="flex flex-1 flex-col overflow-y-auto">
          {/* User */}
          <div className="border-b px-3 py-2" style={{ borderColor: 'var(--border-subtle)' }}>
            <p className="text-xs font-medium text-text-body">{user?.name}</p>
            <p className="text-[10px] text-text-secondary capitalize">{user?.role}</p>
          </div>

          {/* Workflow Steps */}
          {currentProject && (
            <div className="border-b px-3 py-2" style={{ borderColor: 'var(--border-subtle)' }}>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">Workflow</p>
              {STEP_LABELS.map((label, i) => {
                const num = i + 1
                const st = stepStatus(num, currentProject.current_step)
                return (
                  <button
                    key={num}
                    onClick={() => scrollToStep(num)}
                    className="mb-1 flex w-full items-start gap-2 rounded px-2 py-1.5 text-left transition-colors hover:bg-bg-input"
                    // 2026-07-09 - 점등 신호 분리 (이슈 #33 REQ-B7, #24-11):
                    // 진행도 점등(num ≤ current_step)=칩 배경 "불 들어옴", 현재 보기(activeStep)=좌측 액센트 바.
                    style={{
                      ...(num <= currentProject.current_step ? { background: 'var(--bg-chip-purple)' } : {}),
                      ...(activeStep === num ? { boxShadow: 'inset 3px 0 0 var(--color-accent-purple)' } : {}),
                    }}
                  >
                    <span
                      className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold"
                      style={{ background: STATUS_COLORS[st], color: '#1a1b1e' }}
                    >
                      {num}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] font-medium uppercase" style={{ color: STATUS_COLORS[st] }}>
                        {st === 'completed' ? 'COMPLETED' : st === 'in_progress' ? 'IN PROGRESS' : 'PENDING'}
                      </p>
                      <p className="truncate text-[11px] text-text-muted">{label}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {/* My Projects — 폴더 탐색기 (이슈 #77) */}
          <div className="flex flex-1 flex-col px-3 py-2">
            <ProjectExplorer projects={visibleProjects} onOpenProject={openProject}>
            {/* 2026-06-19 - 숨김 프로젝트 펼침 토글 (이슈 #7) */}
            {hiddenProjects.length > 0 && (
              <div data-hidden-section>
                <button
                  onClick={() => setShowHidden((v) => !v)}
                  className="mb-1 mt-1 flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-[11px] text-text-secondary transition-colors hover:bg-bg-input"
                >
                  <span className="text-[9px]">{showHidden ? '▼' : '▶'}</span>
                  <span>{showHidden ? 'Hide hidden' : `Show hidden (${hiddenProjects.length})`}</span>
                </button>
                {showHidden && hiddenProjects.map((p) => renderHiddenButton(p))}
              </div>
            )}
            </ProjectExplorer>
          </div>

          {/* New Project */}
          <div className="border-t p-3" style={{ borderColor: 'var(--border-subtle)' }}>
            <button
              onClick={() => blockIfBusy() || openNewProject(null)}
              disabled={!canWrite}
              title={canWrite ? undefined : READ_ONLY_TITLE}
              className={`w-full rounded-md bg-accent-purple px-3 py-1.5 text-xs font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90 disabled:opacity-50${busy ? ' opacity-50' : ''}`}
            >
              + New Project
            </button>
          </div>
        </div>
      )}
    </aside>
  )
}
